import { faDigits, formatDate, formatMoney } from "@kitchen/ui";
import type { Invoice } from "@kitchen/schemas";

interface PrintInvoiceProps {
  invoice: Invoice;
  organizationName: string;
  onClose: () => void;
}

export function PrintInvoiceView({ invoice, organizationName, onClose }: PrintInvoiceProps) {
  return (
    <div className="print-modal-backdrop">
      <div className="print-modal-actions no-print">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            window.print();
          }}
        >
          چاپ فاکتور (A4)
        </button>
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          بستن
        </button>
      </div>

      <div className="a4-sheet" dir="rtl">
        {/* Header */}
        <div className="print-header">
          <div className="print-kitchen-info">
            <h1 className="print-title">آشپزخانه مرکزی</h1>
            <p className="print-subtitle">سیستم مدیریت یکپارچه و تأمین غذای صنعتی</p>
          </div>
          <div className="print-invoice-meta">
            <div className="meta-row">
              <span className="meta-label">شماره فاکتور:</span>
              <span className="meta-value bold">{faDigits(invoice.number)}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">نوبت ویرایش:</span>
              <span className="meta-value">{faDigits(invoice.revision)}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">تاریخ صدور:</span>
              <span className="meta-value">
                {invoice.issuedAt ? formatDate(invoice.issuedAt.slice(0, 10)) : "پیش‌نویس"}
              </span>
            </div>
          </div>
        </div>

        {/* Customer & Period Info */}
        <div className="print-section print-buyer-box">
          <div className="print-grid">
            <div>
              <span className="label">خریدار / طرف حساب:</span>
              <span className="value bold">{organizationName}</span>
            </div>
            <div>
              <span className="label">دوره مصرف:</span>
              <span className="value">
                از {formatDate(invoice.periodStart)} تا {formatDate(invoice.periodEnd)}
              </span>
            </div>
            <div>
              <span className="label">مبنای محاسبه:</span>
              <span className="value">
                {invoice.basis === "delivered" ? "تعداد تحویل شده" : "تعداد سفارش داده شده"}
              </span>
            </div>
            <div>
              <span className="label">وضعیت:</span>
              <span className="value">{invoice.status === "issued" ? "رسمی / نهایی" : "پیش‌نویس"}</span>
            </div>
          </div>
        </div>

        {/* Lines Table */}
        <table className="print-table">
          <thead>
            <tr>
              <th style={{ width: "35px" }}>ردیف</th>
              <th>تاریخ</th>
              <th>شرح غذا / اقلام</th>
              <th style={{ width: "60px" }}>سفارش</th>
              <th style={{ width: "60px" }}>تحویل</th>
              <th style={{ width: "60px" }}>مبنا</th>
              <th>قیمت واحد (ریال)</th>
              <th>مبلغ کل (ریال)</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((l, idx) => (
              <tr key={l.id}>
                <td className="text-center">{faDigits(idx + 1)}</td>
                <td>{formatDate(l.businessDate)}</td>
                <td>
                  {l.isCompanion ? "مخلفات همراه" : "غذای اصلی"}
                  {l.note ? ` (${l.note})` : ""}
                </td>
                <td className="text-center">{faDigits(l.orderedQuantity)}</td>
                <td className="text-center">
                  {l.deliveredQuantity !== null ? faDigits(l.deliveredQuantity) : "—"}
                </td>
                <td className="text-center bold">{faDigits(l.billedQuantity)}</td>
                <td className="text-left">{faDigits(l.unitPrice.toLocaleString("fa-IR"))}</td>
                <td className="text-left bold">{faDigits(l.totalAmount.toLocaleString("fa-IR"))}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals Summary */}
        <div className="print-totals">
          <div className="totals-row">
            <span>جمع جزء:</span>
            <span>{formatMoney(invoice.subtotal, "rial")}</span>
          </div>
          {invoice.totalDiscount > 0 && (
            <div className="totals-row discount">
              <span>کسورات و تخفیف‌ها:</span>
              <span>− {formatMoney(invoice.totalDiscount, "rial")}</span>
            </div>
          )}
          <div className="totals-row grand-total">
            <span className="bold">مبلغ نهایی قابل پرداخت:</span>
            <span className="bold">{formatMoney(invoice.totalAmount, "rial")}</span>
          </div>
        </div>

        {/* Signatures */}
        <div className="print-signatures">
          <div className="sig-box">
            <span>امضا و مهر صادرکننده (حسابداری آشپزخانه)</span>
          </div>
          <div className="sig-box">
            <span>امضا و تأیید تحویل‌گیرنده (نماینده سازمان)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
